import { Box, Button, Card, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';

import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteQuestionnaireMutation,
	useDeleteQuestionnairesMutation,
	useGetQuestionnaireDetailQuery,
} from '@/api/exercise/day21ApiSlice';
import { useParams } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { showLabel } from '@/utils/helper';
import { SubjectType } from '@/api/enum/common.enum';
import QuestionnaireForm from './components/QuestionnaireForm';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

interface Props {}
export default function Day21QuestionnaireDetailPage(props: Props) {
	const { t } = useTranslation();
	const showModal = useModal();
	const [type, setType] = useState<FormType>(FormType.view);
	const { day21Id } = useParams();
	const [deleteQuestionnaire] = useDeleteQuestionnaireMutation();
	const [deleteQuestionnaires] = useDeleteQuestionnairesMutation();

	const typeList = [
		{ id: 0, name: t('day21.radio') },
		{ id: 1, name: t('day21.multiple') },
		{ id: 2, name: t('day21.line') },
		{ id: 3, name: t('day21.answer') },
	];

	const { data: details, refetch } = useGetQuestionnaireDetailQuery(
		day21Id || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = (id: string) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('day21.deleteQuestionnaire')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('day21.isDeleteQuestionnaire')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteQuestionnaire(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteQuestionnaireSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						refetch();
					}
				}}
			/>
		));
	};
	const handleDeletes = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('day21.deleteQuestionnaires')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('day21.isDeleteQuestionnaires')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteQuestionnaires(day21Id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteQuestionnairesSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						refetch();
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ padding: '0 24px', marginTop: 3, marginBottom: 5 }}>
					<Box>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>
								{t('day21.questionnaireContent')}
							</Typography>
							<Button
								color='secondary'
								component='label'
								endIcon={<DeleteOutlineIcon />}
								onClick={() => {
									handleDeletes();
								}}
							>
								{t('day21.deleteQuestionnaires')}
							</Button>
						</Box>

						{details?.data?.questions?.map((item: any) => (
							<>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid item xs={1}>
										<Typography variant='h6'>
											{t('day21.subject') + item.sort}
										</Typography>
									</Grid>
									<Grid item xs={11}>
										<Grid container spacing={2}>
											<Grid item xs={9}>
												<AppFieldView
													label={t('day21.titleText')}
													value={item.title}
												/>
											</Grid>
											<Grid
												item
												xs
												sx={{
													display: 'flex',
													justifyContent: 'right',
													alignItems: 'right',
												}}
											>
												<Button
													color='secondary'
													component='label'
													sx={{ marginRight: '24px' }}
													endIcon={<DeleteOutlineIcon />}
													onClick={() => {
														handleDelete(item.id);
													}}
												>
													{t('button.delete')}
												</Button>

												<QuestionnaireForm
													type={type}
													setType={setType}
													qaId={item.id}
													info={item}
													refetch={refetch}
													hasQuestion={true}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('day21.optionType')}
													value={showLabel(item.type, typeList)}
												/>
											</Grid>
											{(item.type === SubjectType.Radio ||
												item.type === SubjectType.Multiple) &&
												item.options.map((option: any) => (
													<Grid item xs={12} key={option.id}>
														<AppFieldView
															label={t('day21.option') + option.sort}
															value={option.content}
														/>
													</Grid>
												))}

											{item.type === SubjectType.Linear && (
												<>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.minValue')}
															value={item.minValue}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.maxValue')}
															value={item.maxValue}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.minValueTag')}
															value={item.minValueTag}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.maxValueTag')}
															value={item.maxValueTag}
														/>
													</Grid>
												</>
											)}
										</Grid>
									</Grid>
								</Grid>
								<Divider />
							</>
						))}
					</Box>

					<QuestionnaireForm
						type={FormType.create}
						setType={setType}
						questionsLength={details?.data?.questions?.length}
						hasQuestion={true}
					/>
				</Card>
			</Box>
		</>
	);
}
